import { ChangeEvent } from 'react';
import { Button, Checkbox, Divider, Icon, IconButton } from '@treezor/design-system';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';

import {
  TCardTransactionColumnSetting,
  TCardTransactionColumnKey,
} from '@/features/cards/store/models/card-transactions.model';
import { Accordion } from '@/ui-kit/Accordion';
import { useCardTransactionSettings } from '@/features/cards/hooks/CardTransactions/useCardTransactionSettings';

type TCardTransactionMenuFilterModalProps = {
  title: string;
  isModalOpen: boolean;
  onClose: () => void;
};

export function CardTransactionColumnSettingModal({
  title,
  isModalOpen,
  onClose,
}: TCardTransactionMenuFilterModalProps) {
  const { t } = useTranslation();
  const {
    columns,
    columnOrder,
    getNumberHiddenData,
    handleParentChange,
    handleChildChange,
    handleAll,
    onDragEnd,
    saveSettings,
  } = useCardTransactionSettings();

  const getParentLabel = (parentKey: string) => {
    switch (parentKey) {
      case 'cardtransactionId':
        return t('pages.currentProfiles.cards.transactions.table.headers.paymentMode');
      case 'mccCode':
        return t('pages.currentProfiles.cards.transactions.table.headers.mcc');
      case 'authorizationResponseCode':
        return t('pages.currentProfiles.cards.transactions.table.headers.authorization');
      case 'is3DS':
        return t('pages.currentProfiles.cards.transactions.table.headers.3DSecure');
      default:
        return t(`pages.currentProfiles.cards.transactions.table.headers.${parentKey}`);
    }
  };

  const getLabel = (key: string) => {
    switch (key) {
      case 'merchantCategoryGroup':
      case 'merchantCategoryType':
        return t(`pages.currentProfiles.wallets.operations.metadata.${key}`);
      default:
        return t(`pages.currentProfiles.cards.transactions.table.headers.${key}`);
    }
  };

  const dragIconClassName = 'text-icon-secondary hover:text-icon-primary';

  return (
    <div
      className={`fixed top-0 w-[360px] h-full overflow-x-hidden overflow-y-auto bg-primary shadow-100 rounded-100 flex flex-col z-[201] transition-all ${isModalOpen ? 'right-0' : '-right-1/2'}`}
    >
      <div className="flex flex-col gap-4 pt-7 pb-5 px-7">
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <span className="typo-h6-bold text-secondary">{title}</span>
              {getNumberHiddenData() > 0 && (
                <span className="typo-body-regular-paragraph text-tertiary">
                  {t('pages.currentProfiles.cards.transactions.hiddenData', { count: getNumberHiddenData() })}
                </span>
              )}
            </div>
            <div className="flex items-center justify-start gap-3">
              <Button
                id="modal-action-select-all"
                size="s"
                variant="ghost"
                color="default"
                onClick={() => handleAll(true)}
                testId="filter-select-aLl"
              >
                {t('pages.cores.selectAll')}
              </Button>
              <Divider direction="vertical" className="h-5" />
              <Button
                id="modal-action-clear-all"
                size="s"
                variant="ghost"
                color="default"
                onClick={() => handleAll(false)}
                testId="filter-clear-all"
              >
                {t('pages.cores.clearAll')}
              </Button>
            </div>
          </div>
          <IconButton onClick={onClose} aria-label="close" icon="close" />
        </div>
      </div>
      <div className="overflow-auto h-full pb-5">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="columns-list">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {columnOrder.map((key, index) => {
                  const parentColumn: TCardTransactionColumnSetting = columns[key];
                  if (!parentColumn) return <></>;

                  if (parentColumn?.children) {
                    return (
                      <Draggable key={`draggable-${key}`} draggableId={key} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className="px-7 py-2 border-t border-primary"
                          >
                            <Accordion
                              parentNode={
                                <div className="flex items-center gap-1">
                                  <div {...provided.dragHandleProps} className="w-fit flex items-center">
                                    <Icon size="s" name="drag_indicator" className={dragIconClassName} />
                                  </div>

                                  <Checkbox
                                    name={key}
                                    checked={parentColumn.isActive}
                                    label={getParentLabel(key)}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                      handleParentChange(key as TCardTransactionColumnKey, e.currentTarget.checked)
                                    }
                                  />
                                </div>
                              }
                            >
                              <div className="flex flex-col [&>label]:pl-6 py-2 px-6">
                                {Object.entries(parentColumn.children).map(([childKey, child]) => (
                                  <Checkbox
                                    key={`checkbox-${childKey}`}
                                    name={childKey}
                                    checked={child.isActive as boolean}
                                    label={getLabel(childKey)}
                                    disabled={!parentColumn.isActive}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                      handleChildChange(
                                        key as TCardTransactionColumnKey,
                                        childKey as TCardTransactionColumnKey,
                                        e.currentTarget.checked,
                                      )
                                    }
                                  />
                                ))}
                              </div>
                            </Accordion>
                          </div>
                        )}
                      </Draggable>
                    );
                  }

                  return (
                    <Draggable key={`draggable-${key}`} draggableId={key} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="flex items-center gap-1 px-7 py-2 border-primary border-t"
                        >
                          <div {...provided.dragHandleProps} className="w-fit flex items-center">
                            <Icon size="s" name="drag_indicator" className={dragIconClassName} />
                          </div>
                          <Checkbox
                            name={key}
                            checked={parentColumn.isActive}
                            label={getLabel(key)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              handleParentChange(key as TCardTransactionColumnKey, e.currentTarget.checked)
                            }
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <Divider />
      <div className="flex items-center justify-start px-7 py-5">
        <Button
          id="modal-filter-save"
          variant="primary"
          color="default"
          size="s"
          onClick={() => {
            saveSettings();
            onClose();
          }}
        >
          {t('pages.cores.done')}
        </Button>
      </div>
    </div>
  );
}
